import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { CircleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFetcher } from "react-router-dom";
import { ComboboxOption, deduplicateComboboxOptions } from "@/utils/combobox-options";
import { WithIcon } from "../utils/with-icon";

/** The words the combobox writes itself; French unless the screen around it is English. */
export type ComboboxTexts = {
    unknown: (value: string) => string;
    loading: string;
    empty: string;
    useCustom: (value: string) => string;
    selected: (count: number) => string;
};

const FRENCH_TEXTS: ComboboxTexts = {
    unknown: () => "Valeur inconnue",
    loading: "Chargement...",
    empty: "Aucun résultat",
    useCustom: value => `Utiliser « ${value} »`,
    selected: count => `${count} valeurs sélectionnées`
};

export const ENGLISH_COMBOBOX_TEXTS: ComboboxTexts = {
    unknown: value => `${value} (not in the list)`,
    loading: "Loading…",
    empty: "No result",
    useCustom: value => `Use “${value}”`,
    selected: count => `${count} selected`
};

type ComboboxProps = {
    name: string;
    options?: ComboboxOption[];
    optionsUrl?: string;
    // Remote option shapes are endpoint-specific; callbacks provide their concrete type.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    optionFilter?: (option: any) => boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    optionFormatter?: (option: any) => ComboboxOption;
    defaultValue?: string;
    value?: string;
    valuePrefix?: string;
    multiple?: boolean;
    /** With `multiple`: the most values taken. At 1, a choice replaces the previous one. */
    maxSelected?: number | null;
    multipleSeparator?: string;
    placeholder?: string;
    commandPlaceholder?: string;
    className?: string;
    onValueChange?: (value: string) => void;
    onInputValueChange?: (value: string) => void;
    shouldFilter?: boolean;
    popoverFullWidth?: boolean;
    displaySelectionNames?: boolean;
    disabled?: boolean;
    extraOptions?: { value: string; label?: string }[];
    extraOptionAlert?: boolean;
    keepInnerValue?: boolean;
    allowCustomValue?: boolean;
    texts?: ComboboxTexts;
};

export function Combobox({
    name,
    options,
    optionsUrl,
    optionFilter,
    optionFormatter,
    value,
    valuePrefix,
    defaultValue = "",
    multiple = false,
    maxSelected = null,
    multipleSeparator = ",",
    placeholder = "Select an option",
    commandPlaceholder = "Search an option",
    className = "w-[200px]",
    onValueChange,
    onInputValueChange,
    shouldFilter = true,
    popoverFullWidth = true,
    displaySelectionNames = false,
    disabled = false,
    extraOptions,
    extraOptionAlert = true,
    keepInnerValue,
    allowCustomValue = false,
    texts = FRENCH_TEXTS
}: ComboboxProps) {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [innerOptions, setInnerOptions] = useState(options);
    const [innerValue, setInnerValue] = useState<string | undefined | null>(defaultValue);
    const { data: fetchedOptions, state: fetcherState, load: loadOptions } = useFetcher<unknown[]>();

    const updateValue = (_value: string) => {
        let newValue = innerValue;
        if (multiple) {
            const _values = (innerValue || "").split(multipleSeparator);
            const _valueIndex = _values.indexOf(_value);
            const chosen = _values.filter(v => !!v);
            if (_valueIndex >= 0) {
                _values.splice(_valueIndex, 1);
            } else if (maxSelected === 1) {
                _values.splice(0, _values.length, _value);
            } else if (maxSelected != null && chosen.length >= maxSelected) {
                return;
            } else {
                _values.push(_value);
            }
            newValue = _values.filter(v => !!v).join(multipleSeparator);
        } else {
            newValue = _value === innerValue ? "" : _value; // Uncheck value if it was already selected
        }
        if (!keepInnerValue) setInnerValue(newValue);
        if (onValueChange) {
            onValueChange(newValue);
        }
    };

    useEffect(() => {
        setInnerValue(value || defaultValue);
    }, [defaultValue, value]);

    useEffect(() => {
        if (optionsUrl) {
            loadOptions(optionsUrl);
        }
    }, [loadOptions, optionsUrl]);

    useEffect(() => {
        if (options) {
            setInnerOptions(options);
        }
    }, [options]);

    useEffect(() => {
        if (!open) {
            setSearchValue("");
        }
    }, [open]);

    const trimmedSearch = searchValue.trim();
    const showCustomOption =
        allowCustomValue &&
        trimmedSearch &&
        !(innerOptions ?? []).some(option => option.value.toLowerCase() === trimmedSearch.toLowerCase() || option.label?.toLowerCase() === trimmedSearch.toLowerCase());

    useEffect(() => {
        if (!options) {
            if (Array.isArray(fetchedOptions)) {
                const filteredOptions = fetchedOptions.filter(optionFilter ? optionFilter : () => true);
                setInnerOptions(optionFormatter ? filteredOptions.map(optionFormatter) : (filteredOptions as unknown as ComboboxOption[]));
            } else {
                setInnerOptions(undefined);
            }
        }
    }, [fetchedOptions, optionFilter, optionFormatter, options]);

    const resolvedOptions = useMemo(() => deduplicateComboboxOptions(innerOptions ?? []), [innerOptions]);
    const loading = Boolean(optionsUrl && fetcherState !== "idle");

    return (
        <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
                <Button id={name} type="button" variant="outline" role="combobox" aria-expanded={open} className={cn("justify-between", className)} disabled={disabled}>
                    <span className="truncate">
                        {innerValue ? (
                            <span>
                                {valuePrefix && <span className="mr-1 text-slate-600">{valuePrefix}</span>}
                                {multiple
                                    ? innerValue.split(multipleSeparator).length == 1
                                        ? resolvedOptions.find(option => option.value === innerValue)?.label
                                        : displaySelectionNames
                                          ? innerValue
                                                .split(multipleSeparator)
                                                .map(v => resolvedOptions.find(option => option.value === v)?.label)
                                                .join(`${multipleSeparator} `)
                                          : texts.selected(innerValue.split(multipleSeparator).length)
                                    : resolvedOptions.find(option => option.value === innerValue)?.label ||
                                      (allowCustomValue ? (
                                          innerValue
                                      ) : extraOptionAlert ? (
                                          <WithIcon icon={CircleAlert} className="space-x-2 text-yellow-500">
                                              {extraOptions?.find(option => option.value === innerValue)?.label || texts.unknown(innerValue)}
                                          </WithIcon>
                                      ) : (
                                          extraOptions?.find(option => option.value === innerValue)?.label || texts.unknown(innerValue)
                                      ))}
                            </span>
                        ) : (
                            placeholder
                        )}
                    </span>

                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    <input type="hidden" name={name} value={innerValue || ""} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn("z-[1000] p-0", popoverFullWidth ? "w-[--radix-popover-trigger-width]" : "")}>
                <Command
                    shouldFilter={shouldFilter}
                    filter={(value, search, keywords) => {
                        const extendValue = (value + " " + (keywords || []).join(" ")).toLowerCase();
                        if (extendValue.includes(search.toLowerCase())) return 1;
                        return 0;
                    }}
                >
                    <CommandInput
                        placeholder={commandPlaceholder}
                        value={searchValue}
                        onValueChange={value => {
                            setSearchValue(value);
                            onInputValueChange?.(value);
                        }}
                    />
                    <CommandEmpty>{loading ? texts.loading : texts.empty}</CommandEmpty>
                    <CommandGroup className="max-h-[250px] overflow-auto">
                        {showCustomOption && (
                            <CommandItem
                                key={`custom-${trimmedSearch}`}
                                value={trimmedSearch}
                                keywords={[trimmedSearch]}
                                onSelect={() => {
                                    updateValue(trimmedSearch);
                                    setOpen(false);
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <CheckIcon className="mr-2 h-4 w-4 opacity-0" />
                                    <div>{texts.useCustom(trimmedSearch)}</div>
                                </div>
                            </CommandItem>
                        )}
                        {resolvedOptions.map(option => (
                            <CommandItem
                                key={option.value}
                                value={option.value}
                                keywords={[option.value, ...(option.label ? [option.label] : []), ...(option.keywords ?? [])]}
                                disabled={option.disabled}
                                onSelect={() => {
                                    if (option.disabled) return;
                                    updateValue(option.value);
                                    if (!multiple) {
                                        setOpen(false);
                                    }
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <CheckIcon
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            (multiple ? (innerValue || "").split(multipleSeparator).includes(option.value) : innerValue === option.value) ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div>
                                        <div>{option.search_node ?? option.label ?? option.value}</div>
                                        {option.description && <div className="text-xs text-muted-foreground">{option.description}</div>}
                                    </div>
                                </div>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
